import {Injectable, Optional} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {Role} from '@kion/data-models';
import {HttpClient} from '@angular/common/http';
import {ApiConfig} from '../../../../../api-endpoint/src/lib/api.config';

interface IResponseItem {
  baseStore: string,
  role: {
    uid: string
  }
}

interface SearchResponse {
  roleMappings: IResponseItem[]
}

@Injectable({
  providedIn: 'root'
})
export class RoleConnectors {

  private readonly _rootUrl: string;
  private readonly _baseSite: string

  private route = '/access/users/';

  constructor(
    @Optional() apiConfig: ApiConfig,
    private http: HttpClient,
  ) {
    this._rootUrl = apiConfig.rootUrl;
    this._baseSite = apiConfig.baseSite;
  }

  private static transformFromApi(response: IResponseItem, userId: string): Role {
    return {
      ...response,
      userId: userId,
      id: userId + response.baseStore + response.role.uid
    };
  }

  getRolesByUser(userId: string): Observable<Role[]> {
    return this.http.get<SearchResponse>(this.getOneUrl(userId)).pipe(
      map(
        (response: SearchResponse) => response.roleMappings.map(
          (item: IResponseItem) => RoleConnectors.transformFromApi(item, userId)
        )
      ),
      catchError(error => throwError(error))
    )
  }

  getRolesForCurrentUser(userId: string): Observable<Role[]> {
    return this.http.get<SearchResponse>(this._rootUrl + this._baseSite + '/access/user/roles').pipe(
      map(
        (response: SearchResponse) => response.roleMappings.map(
          (item: IResponseItem) => RoleConnectors.transformFromApi(item, userId)
        )
      ),
      catchError(error => throwError(error))
    )
  }

  changeRoles(userId: string, roles: Partial<Role>[], set: boolean): Observable<Role[]> {
    const body = {
      updatedRoleMappings: roles.map(
        (role: Partial<Role>) => {
          return {
            roleMapping: {
              baseStore: role.baseStore,
              role: {
                uid: role.role.uid
              }
            },
            set: set
          }
        }
      )
    };
    return this.http.post<SearchResponse>(this.getRolesAdminUrl(userId), body).pipe(
      map(
        (response: SearchResponse) => response.roleMappings.map(
          (item: IResponseItem) => RoleConnectors.transformFromApi(item, userId)
        )
      ),
      catchError(error => throwError(error))
    )
  }

  protected getManyUrl() {
    return this._rootUrl + this.route;
  }

  protected getOneUrl(id: string) {
    return this._rootUrl + this._baseSite + this.route + id + '/roles';
  }

  protected getRolesAdminUrl(id: string) {
    return this._rootUrl + 'admin' + this.route + id + '/roles';
  }
}
